import React from 'react'

import { Box, Heading4, Join, Space, useTheme } from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import {
  RecipeUUID,
  Customisations,
  RecipeCustomisation,
  IngredientCustomisation,
} from '@library/api-menu-experience/src/interfaces/recipe'
import { SpicyRecipeOptionsIngredients } from '@library/recipe-customisations'

import { menuRecipeDetailVisibilityChange } from 'routes/Menu/actions/menuRecipeDetails'

import { useTrackVariantListDisplay } from '../../RecipeAlternativeOptions/RecipeAlternativeOptions/useTracking'
import { RecipeOptionsItem } from './RecipeOptionsItem'

import css from './RecipeOptions.css'

const preventPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

type RecipeOptionsProps = {
  customisations?: Customisations
  recipeUuid: RecipeUUID
}

const isRecipeCustomisation = (
  customisation?: Customisations,
): customisation is RecipeCustomisation => customisation?.type === 'recipes'

const isIngredientCustomisation = (
  customisation?: Customisations,
): customisation is IngredientCustomisation => customisation?.type === 'ingredients'

export const RecipeOptions = ({ customisations, recipeUuid }: RecipeOptionsProps) => {
  const { theme } = useTheme()
  const dispatch = useDispatch()
  const handleRecipeChange = (checkedRecipeId: string) => {
    dispatch(menuRecipeDetailVisibilityChange(checkedRecipeId))
  }

  const recipeCustomisations = isRecipeCustomisation(customisations) ? customisations.options : []
  const ingredientSlots = isIngredientCustomisation(customisations) ? customisations.slots : []
  const hasRecipeOptions = recipeCustomisations?.length > 0
  const hasIngredientOptions = ingredientSlots?.length > 0

  useTrackVariantListDisplay({
    hasAlternativeOptions: hasRecipeOptions || hasIngredientOptions,
    view: 'details',
  })

  if (!hasRecipeOptions && !hasIngredientOptions) {
    return null
  }

  if (hasIngredientOptions) {
    return (
      <SpicyRecipeOptionsIngredients
        recipeId={recipeUuid}
        customisation={customisations as IngredientCustomisation}
        view="view_recipe_detail_screen"
        onChangeSelection={handleRecipeChange}
      />
    )
  }

  return (
    <Box style={{ marginBottom: theme.spacing(4) }}>
      <div
        className={css.recipeList}
        role="button"
        tabIndex={-1}
        onClick={preventPropagation}
        onKeyDown={preventPropagation}
      >
        <Join with={<Space size={4} />}>
          <Heading4>Choose an option</Heading4>
          <ul className={css.recipeListText}>
            {recipeCustomisations?.map((option) => (
              <RecipeOptionsItem
                customisationOption={option}
                onChange={() => handleRecipeChange(option.recipeId)}
                key={option.recipeId}
                isChecked={recipeUuid === option.recipeId}
              />
            ))}
          </ul>
        </Join>
      </div>
    </Box>
  )
}
